/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase JSON/RPC boundary; see mem://preferences/lint-debt.md */
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace, type Workspace } from "@/hooks/useWorkspace";
import { tierHasCapability, tierTeamSeatsMax, tierTeamSeatsPerWorkspace } from "@/lib/tier";
import { toast } from "sonner";

export type MemberRole = "owner_admin" | "editor" | "viewer";
export type MemberStatus = "invited" | "active" | "revoked";

export type MemberRow = {
  id: string;
  workspace_id: string;
  member_user_id: string;
  invited_by_user_id: string;
  role: MemberRole;
  status: MemberStatus;
  invited_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
};

export type PendingInvite = {
  id: string;
  invited_email: string;
  role: MemberRole;
  token: string;
  expires_at: string;
};

export const ROLE_LABEL: Record<MemberRole, string> = {
  owner_admin: "Owner / admin",
  editor: "Editor",
  viewer: "Viewer",
};

export const STATUS_LABEL: Record<MemberStatus, string> = {
  invited: "Invited",
  active: "Active",
  revoked: "Revoked",
};

export const formatExpiry = (iso: string) => {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days >= 1) return `expires in ${days}d`;
  const hours = Math.max(1, Math.floor(ms / (60 * 60 * 1000)));
  return `expires in ${hours}h`;
};

/** Loads + mutates workspace members and pending invites for the
 *  team-members card. Includes account-wide seat-cap accounting so
 *  Elite (small allotment) and Agency (unlimited account / per-ws cap)
 *  both enforce the right limits client-side. Server enforces the same
 *  limits in invite-workspace-member. */
export const useWorkspaceMembers = (workspace: Workspace) => {
  const { user } = useAuth();
  const { primaryTier, allOwnedWorkspaces } = useWorkspace();
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [displayMap, setDisplayMap] = useState<
    Record<string, { display_name: string | null; email: string | null }>
  >({});
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [accountSeatUsage, setAccountSeatUsage] = useState<number>(0);

  const isOwner = user?.id === workspace.owner_user_id;
  const hasTeamCapability = tierHasCapability(primaryTier, "team_members");
  const visible = hasTeamCapability && isOwner;

  const accountSeatsMax = tierTeamSeatsMax(primaryTier);
  const perWorkspaceMax = tierTeamSeatsPerWorkspace(primaryTier);

  const workspaceSeatUsage = useMemo(
    () =>
      members.filter((m) => m.status !== "revoked").length + invites.length,
    [members, invites],
  );

  const ownedWorkspaceIds = useMemo(
    () =>
      (allOwnedWorkspaces ?? []).filter((w) => w.owner_user_id === user?.id).map((w) => w.id),
    [allOwnedWorkspaces, user?.id],
  );

  const loadAccountSeats = useCallback(async () => {
    if (!visible || ownedWorkspaceIds.length === 0) {
      setAccountSeatUsage(0);
      return;
    }
    const [{ data: memberRows }, { data: inviteRows }] = await Promise.all([
      supabase
        .from("workspace_members")
        .select("id, status, workspace_id")
        .in("workspace_id", ownedWorkspaceIds)
        .neq("status", "revoked"),
      supabase
        .from("workspace_member_invites")
        .select("id, workspace_id")
        .in("workspace_id", ownedWorkspaceIds)
        .eq("status", "pending"),
    ]);
    setAccountSeatUsage((memberRows?.length ?? 0) + (inviteRows?.length ?? 0));
  }, [visible, ownedWorkspaceIds]);

  const load = useCallback(async () => {
    if (!visible) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("workspace_members")
      .select("id, workspace_id, member_user_id, invited_by_user_id, role, status, invited_at, accepted_at, revoked_at")
      .eq("workspace_id", workspace.id)
      .order("invited_at", { ascending: true });
    if (error) {
      toast.error(error.message);
      setMembers([]);
    } else {
      setMembers((data ?? []) as MemberRow[]);
    }

    const { data: displayRows, error: displayErr } = await supabase.rpc(
      "get_workspace_member_display" as any,
      { _workspace_id: workspace.id },
    );
    if (!displayErr && Array.isArray(displayRows)) {
      const map: Record<string, { display_name: string | null; email: string | null }> = {};
      for (const row of displayRows as Array<{ member_user_id: string; display_name: string | null; email: string | null }>) {
        map[row.member_user_id] = { display_name: row.display_name, email: row.email };
      }
      setDisplayMap(map);
    }

    const { data: inviteRows } = await supabase
      .from("workspace_member_invites")
      .select("id, invited_email, role, token, expires_at")
      .eq("workspace_id", workspace.id)
      .eq("status", "pending")
      .order("created_at", { ascending: false });
    setInvites((inviteRows ?? []) as PendingInvite[]);

    await loadAccountSeats();
    setLoading(false);
  }, [visible, workspace.id, loadAccountSeats]);

  useEffect(() => { load(); }, [load]);

  const accountSeatsRemaining =
    accountSeatsMax === null ? null : Math.max(0, accountSeatsMax - accountSeatUsage);
  const workspaceSeatsRemaining =
    perWorkspaceMax === null ? null : Math.max(0, perWorkspaceMax - workspaceSeatUsage);
  const seatsAvailable =
    (accountSeatsRemaining === null || accountSeatsRemaining > 0) &&
    (workspaceSeatsRemaining === null || workspaceSeatsRemaining > 0);

  const sendInvite = async (email: string, role: MemberRole) => {
    const cleaned = email.trim().toLowerCase();
    if (!cleaned) {
      toast.error("Enter an email address");
      return false;
    }
    if (!seatsAvailable) {
      toast.error(
        accountSeatsRemaining === 0
          ? "You've used all team-member seats on your plan. Revoke a seat or upgrade to add more."
          : `This workspace already has ${perWorkspaceMax} team member${perWorkspaceMax === 1 ? "" : "s"} (the per-workspace limit on your plan).`,
      );
      return false;
    }
    const { data, error } = await supabase.functions.invoke("invite-workspace-member", {
      body: { workspaceId: workspace.id, email: cleaned, role, appOrigin: window.location.origin },
    });
    if (error || (data as any)?.error) {
      toast.error((data as any)?.error ?? error?.message ?? "Failed to send invite");
      return false;
    }
    toast.success((data as any)?.emailSent ? "Invitation sent" : "Invitation created (email pending)");
    load();
    return true;
  };

  const copyInviteLink = async (token: string) => {
    const url = `${window.location.origin}/invite/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Invite link copied");
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };

  const revokeInvite = async (id: string) => {
    if (!confirm("Revoke this pending invite?")) return;
    const { error } = await supabase
      .from("workspace_member_invites")
      .update({ status: "revoked" })
      .eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Invite revoked");
    setInvites((prev) => prev.filter((i) => i.id !== id));
    loadAccountSeats();
  };

  const resendInvite = async (inv: PendingInvite) => {
    setBusyId(inv.id);
    const { error: revokeErr } = await supabase
      .from("workspace_member_invites")
      .update({ status: "revoked" })
      .eq("id", inv.id);
    if (revokeErr) {
      setBusyId(null);
      return toast.error(revokeErr.message);
    }
    const { data, error } = await supabase.functions.invoke("invite-workspace-member", {
      body: { workspaceId: workspace.id, email: inv.invited_email, role: inv.role, appOrigin: window.location.origin },
    });
    setBusyId(null);
    if (error || (data as any)?.error) {
      toast.error((data as any)?.error ?? error?.message ?? "Couldn't resend invite");
      return;
    }
    toast.success((data as any)?.emailSent ? "Invitation resent" : "New invite link created");
    load();
  };

  const updateRole = async (member: MemberRow, role: MemberRole) => {
    if (member.role === role) return;
    setBusyId(member.id);
    const { error } = await supabase.from("workspace_members").update({ role }).eq("id", member.id);
    setBusyId(null);
    if (error) return toast.error(error.message);
    toast.success(`Role updated to ${ROLE_LABEL[role]}`);
    setMembers((prev) => prev.map((m) => (m.id === member.id ? { ...m, role } : m)));
  };

  const revoke = async (member: MemberRow) => {
    if (!confirm(`Revoke access for this member? They will lose access to "${workspace.workspace_label || workspace.display_name || "this workspace"}".`)) return;
    setBusyId(member.id);
    const { error } = await supabase
      .from("workspace_members")
      .update({ status: "revoked", revoked_at: new Date().toISOString() })
      .eq("id", member.id);
    setBusyId(null);
    if (error) return toast.error(error.message);
    toast.success("Access revoked");
    setMembers((prev) =>
      prev.map((m) => (m.id === member.id ? { ...m, status: "revoked", revoked_at: new Date().toISOString() } : m)),
    );
    loadAccountSeats();
  };

  const transferOwnership = async (newOwnerId: string) => {
    const { error } = await supabase.rpc(
      "transfer_workspace_ownership" as any,
      { _workspace_id: workspace.id, _new_owner_id: newOwnerId },
    );
    if (error) {
      toast.error(error.message);
      return false;
    }
    toast.success("Ownership transferred. You're now an Owner / admin member — leave anytime from Account → Workspaces I'm a member of.");
    load();
    return true;
  };

  return {
    user, visible, members, displayMap, invites, loading, busyId,
    load, sendInvite, copyInviteLink, revokeInvite, resendInvite,
    updateRole, revoke, transferOwnership,
    // seat accounting
    accountSeatsMax,
    accountSeatUsage,
    accountSeatsRemaining,
    perWorkspaceMax,
    workspaceSeatUsage,
    workspaceSeatsRemaining,
    seatsAvailable,
  };
};
